import { useRef, useState } from "react";
import PaymentChevronDown from "@Icons/PaymentChevronDown";
import Tooltip from "@Components/Tooltip/Tooltip";
import InfoIcon from "@Icons/InfoIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import getCurrencySymbol from "helpers/getCurrencySymbol";
import PropertyDetailsModal from "../../../PropertyDetailsModal";
import PolicyIconContainer from "../../../shared/PolicyIconContainer";
import { POLICY_ICON_PROPS } from "../../../shared/policyIconConstants";
import { FeeCategory, FeeItem, FeesSectionData, getFeeItemLabel } from "../../types";
import styles from "./FeesAccordionSection.module.scss";
import FeeSectionIcon from "@Icons/FeeSectionIcon";

type FeeDetailModalState = {
  label: string;
  content: string;
};

type FeesAccordionSectionProps = {
  data: FeesSectionData;
  isDesktop?: boolean;
  baseCurrency?: string;
  pricingDuration?: string;
};

const FREQUENCY_LABELS: Record<string, string> = {
  one_time: "One-time",
  monthly: "Monthly",
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  quarterly: "Quarterly",
  annually: "Annually",
  per_semester: "Per Semester",
  per_installment: "Per Installment",
  per_instalment: "Per Installment",
  per_occurrence: "Per Occurrence",
};

const PRICING_DURATION_TO_FREQUENCY: Record<string, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
};

const formatFrequencyLabel = (frequency: string) => {
  const normalized = frequency.toLowerCase().replace(/\s+/g, "_");
  return FREQUENCY_LABELS[normalized] || frequency;
};

const resolveFrequencyLabel = (item: FeeItem, categoryId?: string, pricingDuration?: string) => {
  if (item.frequency) return formatFrequencyLabel(item.frequency);

  if (categoryId === "recurring" && pricingDuration) {
    return PRICING_DURATION_TO_FREQUENCY[pricingDuration.toLowerCase()] ?? null;
  }

  return null;
};

const formatFeeAmount = (item: FeeItem, baseCurrency?: string) => {
  if (item.pricing_type === "usage") return "Varies by usage";
  const symbol = getCurrencySymbol(baseCurrency);
  if (item.amount != null) return `${symbol}${item.amount}`;
  if (item.min_amount != null && item.max_amount != null) {
    return `${symbol}${item.min_amount} - ${symbol}${item.max_amount}`;
  }
  if (item.min_amount != null) return `${symbol}${item.min_amount}`;
  if (item.max_amount != null) return `${symbol}${item.max_amount}`;
  return "";
};

const FeeRow = ({
  item,
  baseCurrency,
  showFrequency = true,
  categoryId,
  pricingDuration,
  isDesktop = false,
  onOpenFeeDetail,
}: {
  item: FeeItem;
  baseCurrency?: string;
  showFrequency?: boolean;
  categoryId?: string;
  pricingDuration?: string;
  isDesktop?: boolean;
  onOpenFeeDetail?: (detail: FeeDetailModalState) => void;
}) => {
  const frequencyLabel = showFrequency
    ? resolveFrequencyLabel(item, categoryId, pricingDuration)
    : null;
  const feeLabel = getFeeItemLabel(item);
  const hasTooltip = Boolean(item.tooltip);
  // Anchors the desktop tooltip tip to the info icon while the whole label row stays hoverable.
  const feeInfoIconRef = useRef<HTMLSpanElement>(null);
  const label = (
    <span
      className={formatClassNames(styles.feeLabel, hasTooltip && !isDesktop && styles.dottedLabel)}
    >
      {feeLabel}
    </span>
  );

  const openFeeDetail = (content: string) => {
    onOpenFeeDetail?.({ label: feeLabel, content });
  };

  const renderFeeLabel = () => {
    if (!hasTooltip || !item.tooltip) return label;

    if (isDesktop) {
      return (
        <Tooltip
          content={item.tooltip}
          direction="bottom"
          delay={200}
          usePortal
          anchorRef={feeInfoIconRef}
          classNameWrapper={styles.feeTooltipWrapper}
          className={styles.feeTooltip}
        >
          <span
            className={styles.feeLabelWithInfo}
            aria-label={`More information about ${feeLabel}`}
          >
            {label}
            <span ref={feeInfoIconRef} className={styles.feeInfoIcon}>
              <InfoIcon height="14" width="14" fill="#6b7280" />
            </span>
          </span>
        </Tooltip>
      );
    }

    return (
      <button
        type="button"
        className={styles.feeLabelButton}
        onClick={() => openFeeDetail(item.tooltip as string)}
      >
        {label}
      </button>
    );
  };

  const renderNationalityNote = () => {
    if (!item.nationality_note) return null;

    if (isDesktop) {
      return (
        <Tooltip
          content={item.nationality_note}
          direction="bottom"
          delay={200}
          usePortal
          classNameWrapper={styles.nationalityNoteWrapper}
          className={styles.nationalityNoteTooltip}
        >
          <span className={styles.nationalityNoteIcon} aria-label="Nationality-based fee note">
            🌐
          </span>
        </Tooltip>
      );
    }

    return (
      <button
        type="button"
        className={styles.nationalityNoteButton}
        onClick={() => openFeeDetail(item.nationality_note as string)}
        aria-label="Nationality-based fee note"
      >
        <span className={styles.nationalityNoteIcon}>🌐</span>
      </button>
    );
  };

  return (
    <div className={styles.feeRow}>
      <span className={styles.feeLabelWrapper}>
        {renderFeeLabel()}
        {renderNationalityNote()}
      </span>
      <span className={styles.feeAmount}>
        {formatFeeAmount(item, baseCurrency)}
        {frequencyLabel ? (
          <span className={styles.feeFrequency}>
            <span className={styles.bullet}>•</span>
            {frequencyLabel}
          </span>
        ) : null}
        {item.max_quantity != null && item.max_quantity > 0 ? (
          <span className={styles.feeFrequency}>
            <span className={styles.bullet}>•</span>
            Max {item.max_quantity}
          </span>
        ) : null}
      </span>
    </div>
  );
};

const FeeCategoryCard = ({
  category,
  isDesktop,
  baseCurrency,
  pricingDuration,
  defaultExpanded = false,
  showFrequency = true,
  onOpenFeeDetail,
}: {
  category: FeeCategory;
  isDesktop: boolean;
  baseCurrency?: string;
  pricingDuration?: string;
  defaultExpanded?: boolean;
  showFrequency?: boolean;
  onOpenFeeDetail?: (detail: FeeDetailModalState) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(
    defaultExpanded || Boolean(category.defaultExpanded),
  );

  const toggle = () => setIsExpanded((prev) => !prev);

  return (
    <div className={styles.feeCard}>
      <div
        className={formatClassNames(styles.feeCardHeader, isExpanded && styles.expanded)}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <div className={styles.feeCardInfo}>
          <div className={styles.feeCardTitle}>{category.title}</div>
          {category.subtitle ? (
            <div className={styles.feeCardSubtitle}>{category.subtitle}</div>
          ) : null}
        </div>
        <div className={formatClassNames(styles.chevron, isExpanded && styles.chevronExpanded)}>
          <PaymentChevronDown width={isDesktop ? 16 : 12} height={isDesktop ? 16 : 12} />
        </div>
      </div>

      {isExpanded && (
        <div className={styles.feeCardBody}>
          {category.subgroups?.map((subgroup, index) => (
            <div key={subgroup.id} className={styles.subgroup}>
              {index > 0 && <div className={styles.subgroupDivider} />}
              <div className={styles.subgroupTitle}>{subgroup.title}</div>
              <div className={styles.feeList}>
                {subgroup.items.map((feeItem) => (
                  <FeeRow
                    key={feeItem.id}
                    item={feeItem}
                    baseCurrency={baseCurrency}
                    showFrequency={showFrequency}
                    categoryId={category.id}
                    pricingDuration={pricingDuration}
                    isDesktop={isDesktop}
                    onOpenFeeDetail={onOpenFeeDetail}
                  />
                ))}
              </div>
              {subgroup.note && <p className={styles.feeNote}>{subgroup.note}</p>}
            </div>
          ))}

          {category.items && category.items.length > 0 && (
            <div className={styles.feeList}>
              {category.items.map((feeItem) => (
                <FeeRow
                  key={feeItem.id}
                  item={feeItem}
                  baseCurrency={baseCurrency}
                  showFrequency={showFrequency}
                  categoryId={category.id}
                  pricingDuration={pricingDuration}
                  isDesktop={isDesktop}
                  onOpenFeeDetail={onOpenFeeDetail}
                />
              ))}
            </div>
          )}

          {category.note && <p className={styles.feeNote}>{category.note}</p>}
        </div>
      )}
    </div>
  );
};

const FeesAccordionSection = ({
  data,
  isDesktop = false,
  baseCurrency,
  pricingDuration,
}: FeesAccordionSectionProps) => {
  const [feeDetailModal, setFeeDetailModal] = useState<FeeDetailModalState | null>(null);

  if (!data?.categories?.length) return null;

  // In Required Fees, the frequency label (Weekly, Monthly, One-time, ...) is only meaningful for
  // recurring charges, so only show it for the "recurring" category. Add-On Fees show it for all
  // categories.
  const isRequiredFees = data.id === "required_fees";

  return (
    <>
      <div
        className={formatClassNames(
          styles.section,
          isDesktop ? styles.desktopSection : styles.mobileSection,
        )}
      >
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>
            <PolicyIconContainer>
              <FeeSectionIcon {...POLICY_ICON_PROPS} />
            </PolicyIconContainer>
          </span>
          <h3 className={styles.sectionTitle}>{data.title}</h3>
        </div>

        <div className={styles.categories}>
          {data.categories.map((category, index) => (
            <FeeCategoryCard
              key={category.id}
              category={category}
              isDesktop={isDesktop}
              baseCurrency={baseCurrency}
              pricingDuration={pricingDuration}
              defaultExpanded={index === 0}
              showFrequency={!isRequiredFees || category.id === "recurring"}
              onOpenFeeDetail={isDesktop ? undefined : setFeeDetailModal}
            />
          ))}
        </div>
      </div>

      {feeDetailModal ? (
        <PropertyDetailsModal
          header={feeDetailModal.label}
          actionButtonText="Got it"
          hidePropertyModal={() => setFeeDetailModal(null)}
          isDesktop={false}
          fitContent
        >
          <p className={styles.feeDetailContent}>{feeDetailModal.content}</p>
        </PropertyDetailsModal>
      ) : null}
    </>
  );
};

export default FeesAccordionSection;
